import { useState, SetStateAction, Dispatch, useEffect } from "react";
import { useRouter } from "next/router";
import urlParameterController from "../util/urlParameterController";

const getInitialState = (urlOptions: string, allowedOptions: string[]) => {
  return allowedOptions.reduce((accumulator, currentValue) => {
    if (urlOptions && urlOptions.includes(currentValue)) {
      accumulator[currentValue] = true;
    } else {
      accumulator[currentValue] = false;
    }
    return accumulator;
  }, {});
};

interface FilterArgs {
  filterName: string;
  initVal: string;
  selectAllInit?: Record<string, boolean>;
  allowedOptions: string[];
  isOpen: boolean;
  setIsOpen: Dispatch<SetStateAction<boolean>>;
}

export function useMultiSelectFilterState(args: FilterArgs) {
  const {
    filterName,
    initVal,
    selectAllInit,
    allowedOptions,
    isOpen,
    setIsOpen,
  } = args;
  const router = useRouter();
  const initState = getInitialState(initVal, allowedOptions);
  const [currentSelection, setCurrentSelection] = useState(initState);
  const [appliedSelection, setAppliedSelection] = useState(initState);
  const [selectAllState, setSelectAllState] = useState({ ...selectAllInit });

  useEffect(() => {
    updateSelectAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentSelection]);

  const updateSelectAll = () => {
    const selectAllTerms = Object.keys(selectAllState).map((t) => t.slice(4));

    const notSelected = [];
    for (const [key, value] of Object.entries(currentSelection)) {
      if (value === false) {
        notSelected.push(key.slice(4));
      }
    }

    const newSelectAllState = {};

    selectAllTerms.forEach((element) => {
      if (!notSelected.includes(element)) {
        newSelectAllState["all-" + element] = true;
      } else {
        newSelectAllState["all-" + element] = false;
      }
    });

    setSelectAllState(newSelectAllState);
  };

  const resetValues = () => {
    return allowedOptions.reduce((accumulator, currentValue) => {
      accumulator[currentValue] = false;
      return accumulator;
    }, {});
  };

  const handleToggle = () => {
    setCurrentSelection(appliedSelection);
    setIsOpen(!isOpen);
  };

  const handleChange = ({ value, checkboxState }) => {
    setCurrentSelection({
      ...currentSelection,
      [value]: checkboxState,
    });
  };

  const handleSelectAll = ({ value, checkboxState }) => {
    /**
     * expects "select all" keys to have start with "all-"" followed by
     * the common term used on all keys to be included in selection
     * e.g. "all-monday" to match "monday-morning", "monday-afternoon", "monday-evening"
     **/
    const selectAllTerm = value.slice(4); // removes "all-"
    setSelectAllState({
      ...selectAllState,
      [value]: checkboxState,
    });

    const newValues = {};
    for (const item in currentSelection) {
      if (item.includes(selectAllTerm)) {
        newValues[item] = checkboxState;
      }
    }

    setCurrentSelection({
      ...currentSelection,
      ...newValues,
    });
  };

  const handleSubmit = (separator = "_") => {
    handleToggle();
    setAppliedSelection(currentSelection);
    const activeFilters = allowedOptions.filter(
      (option) => currentSelection[option]
    );
    const filterParams = activeFilters.join(separator);
    const sanitisedLabel = filterParams.toLowerCase();
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: "GAevent",
      event_id: "14",
      eventCategory: "filter",
      eventAction: filterName,
      eventLabel: sanitisedLabel,
    });

    let newPath = urlParameterController(
      filterName.toUpperCase(),
      filterParams,
      router.asPath
    );
    newPath = urlParameterController("PAGINATION", "1", newPath); //Reset pagination to page=1
    router.push(newPath);
  };

  const handleReset = (e) => {
    e.preventDefault();
    setCurrentSelection(resetValues);
    setAppliedSelection(resetValues);
    setSelectAllState({ ...selectAllInit });
    let newPath = urlParameterController(
      filterName.toUpperCase(),
      "",
      router.asPath
    );
    newPath = urlParameterController("PAGINATION", "1", newPath); //Reset pagination to page=1
    router.push(newPath);
  };

  return {
    currentSelection,
    setCurrentSelection,
    appliedSelection,
    handleChange,
    selectAllState,
    handleSelectAll,
    handleSubmit,
    handleReset,
    handleToggle,
    isOpen,
    setIsOpen,
  };
}
